import { JOB_PHASE_COST_TOTAL_MAX } from '@vatos-pas/common';

import { formatCurrency, toNumber } from 'utils/currency';

/** Resource the Total Cost cell PATCHes; the row id is the job phase id. */
export const TOTAL_COST_RESOURCE = 'job-phase';

/** Mirrors the job_phase column, numeric(7,2): above this Postgres overflows. */
export const TOTAL_COST_MAX = JOB_PHASE_COST_TOTAL_MAX;

export const TOTAL_COST_SAVED_MESSAGE = 'Total Cost saved successfully.';
export const TOTAL_COST_FAILED_MESSAGE =
  'Unable to save Total Cost. Please try again later.';

// Same wording family as the Production Receivables PO Amount messages.
export const TOTAL_COST_NEGATIVE_MESSAGE =
  'Cost Override must be greater than or equal to 0';
export const TOTAL_COST_MAX_MESSAGE = `Cost Override must not exceed ${formatCurrency(
  TOTAL_COST_MAX,
)}`;
export const TOTAL_COST_DECIMALS_MESSAGE =
  'Cost Override accepts up to two decimal places';
export const TOTAL_COST_NOT_A_NUMBER_MESSAGE = 'Cost Override must be a number';

const NUMERIC_TEXT = /^-?(\d+(\.\d*)?|\.\d+)$/;
/** Digits grouped by thousands, as the cell itself prints them: 1,250.50. */
const GROUPED_TEXT = /^-?\d{1,3}(,\d{3})+(\.\d*)?$/;

/**
 * Text as typed, reduced to what the numeric rules look at: surrounding
 * spaces and a leading "$" dropped, thousands separators removed when they
 * are placed the way the cell prints them. Anything else is left alone so
 * that validation reports it.
 */
const normalizeTotalCost = (raw: string): string => {
  const text = raw.trim().replace(/^\$\s*/, '');
  return GROUPED_TEXT.test(text) ? text.replace(/,/g, '') : text;
};

/** The cell at rest: currency with two decimals, blank when nothing is stored. */
export const formatTotalCost = (
  value: number | string | null | undefined,
): string => formatCurrency(value, '');

/** The cell while editing: "1250.00", blank when nothing is stored. */
export const formatTotalCostForInput = (
  value: number | string | null | undefined,
): string => {
  const num = toNumber(value);
  return num === null ? '' : num.toFixed(2);
};

/**
 * Inline error for the text typed in the cell, null when it can be submitted.
 * Blank is valid and clears the value. Zero or greater, up to two decimal
 * places, numbers only, at most the column ceiling. The cell's own display
 * format ("$1,250.00") is accepted, so a value copied from one row can be
 * pasted into another.
 */
export const validateTotalCost = (raw: string): string | null => {
  const text = normalizeTotalCost(raw);
  if (text === '') return null;
  if (!NUMERIC_TEXT.test(text)) return TOTAL_COST_NOT_A_NUMBER_MESSAGE;
  if (text.startsWith('-')) return TOTAL_COST_NEGATIVE_MESSAGE;

  const decimals = text.split('.')[1] ?? '';
  if (decimals.length > 2) return TOTAL_COST_DECIMALS_MESSAGE;

  if (Number(text) > TOTAL_COST_MAX) return TOTAL_COST_MAX_MESSAGE;
  return null;
};

/** Value to store for valid text: null clears, otherwise the number as typed. */
export const parseTotalCost = (raw: string): number | null => {
  const text = normalizeTotalCost(raw);
  if (text === '') return null;
  return Number(Number(text).toFixed(2));
};
